import React, { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { ShieldCheck, Cookie } from "lucide-react";
import AppLayout from "../components/AppLayout";

const Section = ({ n, title, children }) => (
    <section className="mt-8">
        <h2 className="font-display uppercase text-lg md:text-lg text-nsp-green tracking-tight flex items-baseline gap-2">
            <span className="text-nsp-yellow-dark">{n}.</span> {title}
        </h2>
        <div className="mt-2 text-sm leading-relaxed text-zinc-700 space-y-2">{children}</div>
    </section>
);

const PrivacyPage = () => {
    const { hash } = useLocation();
    useEffect(() => {
        if (hash) {
            const el = document.querySelector(hash);
            if (el) el.scrollIntoView({ behavior: "smooth" });
        } else {
            window.scrollTo(0, 0);
        }
    }, [hash]);

    return (
        <AppLayout>
            <div className="max-w-3xl mx-auto px-5 sm:px-8 py-10 md:py-16" data-testid="privacy-page">
                <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-2xl bg-nsp-green flex items-center justify-center">
                        <ShieldCheck className="w-6 h-6 text-nsp-yellow" />
                    </div>
                    <div>
                        <h1 className="font-display uppercase text-3xl sm:text-4xl text-nsp-green tracking-tighter leading-none">
                            Informativa Privacy
                        </h1>
                        <p className="text-zinc-500 text-xs mt-1 uppercase tracking-wider">
                            ai sensi degli artt. 13 e 14 del Regolamento UE 2016/679 ("GDPR")
                        </p>
                    </div>
                </div>

                <p className="mt-6 text-sm text-zinc-600 leading-relaxed">
                    La presente informativa descrive il trattamento dei dati personali effettuato da
                    Nolo Service Point Soc. Coop. attraverso il sito internet e l'applicazione web
                    (di seguito "Sito/App"), nonché nell'ambito dei rapporti di noleggio.
                </p>

                <Section n="1" title="Titolare del trattamento">
                    <p>
                        <b>Nolo Service Point Soc. Coop.</b>
                        <br /> Sede legale: Largo Montedinove n. 4 – 00138 Roma (RM)
                        <br /> Sede operativa: Via Salaria Vecchia n. 29 – 02037 Osteria Nuova di Poggio Moiano (RI)
                        <br /> P.IVA/C.F.: 12162641000 – REA: RM-1355223
                        <br /> Tel.: 0765819039 – Numero: 327 2237610 (anche WhatsApp)
                        <br /> E-mail: <a href="mailto:privacy@noloservicepoint.it" className="text-nsp-green underline font-semibold">privacy@noloservicepoint.it</a>
                        <br /> PEC: <a href="mailto:nsp.soccoop@legalmail.it" className="text-nsp-green underline font-semibold">nsp.soccoop@legalmail.it</a>
                    </p>
                </Section>

                <Section n="2" title="Dati trattati">
                    <p>
                        Dati anagrafici e di contatto (nome, e-mail, telefono, azienda), dati fiscali e
                        societari, dati relativi ai contratti di noleggio (mezzi noleggiati, periodi,
                        cauzioni, pagamenti), nominativi e attestati di formazione dei lavoratori
                        incaricati dell'uso delle attrezzature (obbligo ex art. 72, comma 2, D.Lgs.
                        81/2008), dati di navigazione del Sito/App.
                    </p>
                    <p>
                        Tramite il Sito/App vengono inoltre trattati: i dati inseriti nei <b>moduli di
                        richiesta preventivo</b> e di <b>richiesta corsi di formazione</b>; il contenuto dei
                        messaggi inviati all'<b>assistente virtuale (chatbot)</b>; l'eventuale consenso
                        alla ricezione di <b>notifiche push</b>.
                    </p>
                </Section>

                <Section n="3" title="Finalità e basi giuridiche">
                    <p>
                        a) Gestione di richieste, preventivi e contratti di noleggio – <i>esecuzione del
                        contratto o di misure precontrattuali (art. 6.1.b GDPR)</i>
                        <br /> b) Adempimenti di legge, fiscali e in materia di sicurezza sul lavoro
                        (D.Lgs. 81/2008) – <i>obbligo legale (art. 6.1.c)</i>
                        <br /> c) Valutazioni economico-finanziarie e tutela del credito – <i>legittimo
                        interesse (art. 6.1.f)</i>
                        <br /> d) Invio di comunicazioni commerciali e promozionali – <i>consenso
                        (art. 6.1.a), revocabile in ogni momento</i>
                        <br /> e) Funzionamento del Sito/App e sicurezza informatica – <i>legittimo
                        interesse (art. 6.1.f)</i>
                    </p>
                </Section>

                <Section n="4" title="Natura del conferimento">
                    <p>
                        Il conferimento dei dati per le finalità a) e b) è necessario: in mancanza non
                        sarà possibile evadere la richiesta o concludere il contratto. Il conferimento
                        per finalità di marketing è facoltativo e non pregiudica in alcun modo la
                        fruizione dei servizi.
                    </p>
                </Section>

                <Section n="5" title="Destinatari">
                    <p>
                        I dati potranno essere comunicati a categorie di destinatari quali: fornitori e
                        partner tecnici (nazionali ed esteri) che erogano servizi informatici, di hosting
                        e di comunicazione; istituti bancari; compagnie assicurative; consulenti fiscali
                        e legali; autorità competenti quando previsto per legge. I soggetti che trattano
                        dati per conto del Titolare sono nominati responsabili del trattamento ex art.
                        28 GDPR. I dati non sono oggetto di diffusione.
                    </p>
                </Section>

                <Section n="6" title="Trasferimenti extra-UE">
                    <p>
                        Alcuni fornitori tecnici (ad es. servizi di hosting, assistente virtuale, notifiche
                        push) potrebbero trattare dati in Paesi extra-UE. In tali casi il trasferimento
                        avviene sulla base di decisioni di adeguatezza della Commissione Europea o di
                        Clausole Contrattuali Standard (SCC) ex art. 46 GDPR.
                    </p>
                </Section>

                <Section n="7" title="Conservazione">
                    <p>
                        I dati contrattuali sono conservati per 10 anni dalla cessazione del rapporto
                        (obblighi civilistici e fiscali); le dichiarazioni ex D.Lgs. 81/2008 per tutta la
                        durata del noleggio e nei termini di legge; i dati raccolti tramite i moduli del
                        Sito/App per il tempo necessario a evadere la richiesta e comunque non oltre 24
                        mesi; i dati per marketing fino a revoca del consenso.
                    </p>
                </Section>

                <Section n="8" title="Diritti dell'interessato">
                    <p>
                        Ai sensi degli artt. 15-22 GDPR è possibile esercitare in ogni momento i diritti
                        di accesso, rettifica, cancellazione, limitazione, portabilità e opposizione, nonché
                        revocare i consensi prestati, scrivendo a{" "}
                        <a href="mailto:privacy@noloservicepoint.it" className="text-nsp-green underline font-semibold">privacy@noloservicepoint.it</a>{" "}
                        o a{" "}
                        <a href="mailto:nsp.soccoop@legalmail.it" className="text-nsp-green underline font-semibold">nsp.soccoop@legalmail.it</a>.
                        È inoltre possibile proporre reclamo al Garante per la protezione dei dati
                        personali (<a href="https://www.garanteprivacy.it" target="_blank" rel="noreferrer" className="text-nsp-green underline font-semibold">www.garanteprivacy.it</a>).
                    </p>
                </Section>

                <Section n="9" title="Processi decisionali automatizzati">
                    <p>
                        Il Titolare non effettua profilazione né processi decisionali interamente
                        automatizzati che producano effetti giuridici sugli interessati. L'assistente
                        virtuale del Sito/App fornisce risposte automatiche a scopo informativo: le
                        conversazioni possono essere elaborate da fornitori di servizi di intelligenza
                        artificiale in qualità di responsabili del trattamento; si raccomanda di non
                        inserire in chat dati non necessari.
                    </p>
                </Section>

                <Section n="10" title="Dati dei lavoratori (D.Lgs. 81/2008)">
                    <p>
                        In qualità di noleggiatore, NSP acquisisce e conserva per tutta la durata del
                        noleggio la dichiarazione del Datore di Lavoro con i nominativi dei lavoratori
                        incaricati dell'uso delle attrezzature e l'attestazione della relativa formazione
                        (art. 72, comma 2, D.Lgs. 81/2008). Tali dati, riferiti anche a soggetti terzi,
                        sono trattati esclusivamente per l'adempimento di tale obbligo legale. Referente
                        per chiarimenti: tel. 3482933276 (Francesco) – nsp.soccoop@legalmail.it.
                    </p>
                </Section>

                {/* ================= COOKIE POLICY ================= */}
                <div id="cookie" className="mt-14 pt-10 border-t-2 border-nsp-green/15" data-testid="cookie-policy">
                    <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-2xl bg-nsp-yellow flex items-center justify-center">
                            <Cookie className="w-6 h-6 text-nsp-green" />
                        </div>
                        <h1 className="font-display uppercase text-3xl text-nsp-green tracking-tighter leading-none">
                            Cookie Policy
                        </h1>
                    </div>

                    <div className="mt-6 text-sm leading-relaxed text-zinc-700 space-y-4">
                        <p>
                            Il Sito/App <b>non utilizza cookie di profilazione né cookie analitici di terze
                            parti</b> e non richiede pertanto banner di consenso ai sensi delle Linee guida
                            del Garante (10 giugno 2021).
                        </p>
                        <p>Vengono utilizzati esclusivamente strumenti tecnici, necessari al funzionamento:</p>
                        <ul className="list-disc pl-5 space-y-2">
                            <li>
                                <b>Memorizzazione locale (localStorage/sessionStorage)</b>: salvataggio
                                della bozza del preventivo sul dispositivo dell'utente, identificativo
                                tecnico della sessione di chat e preferenze di interfaccia. Questi dati
                                restano sul dispositivo, non vengono usati per tracciamento e possono
                                essere eliminati in qualsiasi momento cancellando i dati di navigazione
                                del browser.
                            </li>
                            <li>
                                <b>Service worker (PWA)</b>: memorizzazione tecnica in cache di risorse
                                del Sito/App per il funzionamento offline e prestazioni.
                            </li>
                            <li>
                                <b>Notifiche push (facoltative)</b>: attivate solo previo consenso
                                esplicito dell'utente tramite il browser; il consenso è revocabile in ogni
                                momento dalle impostazioni del browser o del dispositivo.
                            </li>
                        </ul>
                        <p>
                            Per qualsiasi domanda sui cookie e sugli strumenti tecnici utilizzati:{" "}
                            <a href="mailto:privacy@noloservicepoint.it" className="text-nsp-green underline font-semibold">
                                privacy@noloservicepoint.it
                            </a>.
                        </p>
                    </div>
                </div>

                <p className="mt-12 text-[11px] text-zinc-400">
                    Ultimo aggiornamento: giugno 2026. La presente informativa può essere aggiornata;
                    eventuali modifiche sostanziali saranno pubblicate su questa pagina.
                </p>
            </div>
        </AppLayout>
    );
};

export default PrivacyPage;
